import { useLoaderData } from 'react-router';
import { localizePortabilityError } from '../../../common/utils/phoneNumberUtils.js';
import { ValidatePortableResponse } from '../../../generated/api/validatePortableResponse';
import {
  SubscriptionConfigField,
  getDefaultSubscriptionConfigFormState,
  getFieldsToShow,
} from '../utils/subscriptionConfigurationUtils.js';
import { useSubscriptionConfigFormBase } from './useSubscriptionConfigFormBase.js';
import type { UseSubscriptionConfigFormResult } from './useSubscriptionConfigFormBase.js';
import type { PlaceOrderLoaderData } from '../../../common/loaders.js';
import type { BasketItem } from '../../../common/types/shoppingBasket.js';
import type { OnlineModel } from '../../../generated/api/onlineModel.js';

// Re-export shared types so existing imports from this file keep working
export type { InternalFormData, UseSubscriptionConfigFormResult } from './useSubscriptionConfigFormBase.js';

export interface UseSubscriptionConfigFormOptions {
  cartItemInstanceId: string;
  onlineModel: OnlineModel;
  basketItem: BasketItem;
  mdmId: string;
  phoneNumbers: string[];
}

/**
 * Hook to manage subscription configuration form state, validation, and sync with parent form.
 */
export const useSubscriptionConfigForm = ({
  cartItemInstanceId,
  onlineModel,
  basketItem,
  mdmId,
  phoneNumbers,
}: UseSubscriptionConfigFormOptions): UseSubscriptionConfigFormResult => {
  const { holidays } = useLoaderData() as PlaceOrderLoaderData;

  const fieldsList = getFieldsToShow(onlineModel);
  const fieldsToShow = {
    showSelectPhoneNumber: fieldsList.includes(SubscriptionConfigField.SELECT_PHONE_NUMBER),
    showNumberPrivacy: fieldsList.includes(SubscriptionConfigField.NUMBER_PRIVACY),
    showSimCard: fieldsList.includes(SubscriptionConfigField.SIMCARD),
    showM2MSimCard: fieldsList.includes(SubscriptionConfigField.M2M_SIMCARD),
  };

  return useSubscriptionConfigFormBase({
    cartItemInstanceId,
    basketItemId: basketItem.id,
    mdmId,
    phoneNumbers,
    holidays,
    fieldsToShow,
    getDefaultFormStateFallback: () => getDefaultSubscriptionConfigFormState(onlineModel, holidays),
    localizePhoneError: (result: ValidatePortableResponse) => {
      if (
        !result.portable &&
        result.errorType &&
        result.errorType !== ValidatePortableResponse.ErrorTypeEnum.TECHNICAL_ERROR
      ) {
        return localizePortabilityError(result.errorType);
      }
      return undefined;
    },
    isNewPhoneRequired: () => true,
  });
};
